import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { collection, query, where, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { 
  Home,
  Users, 
  UserCheck, 
  UserX, 
  Truck, 
  LayoutDashboard, 
  ArrowLeft,
  School,
  FileText,
  Upload,
  UserMinus,
  UserPlus,
  UserCog,
  Settings as SettingsIcon,
  Search,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { motion } from 'motion/react';
import { refreshDailyStats, getDailyStats, getLongTermAbsences } from '../services/statsService';
import { safeStorage } from '../services/safeStorage';
import { useAcademicYear } from '../context/AcademicYearContext';

// Simple in-memory cache for dashboard stats by academic year to reduce reads across tab switches
let cachedStatsByYear: Record<string, DashboardStats> = {};
let cachedSummariesByYear: Record<string, ClassAttendanceSummary[]> = {};
let lastFetchTimeByYear: Record<string, number> = {};
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache

export const clearDashboardCache = () => {
  cachedStatsByYear = {};
  cachedSummariesByYear = {};
  lastFetchTimeByYear = {};
};

interface DashboardStats {
  totalStudents: number;
  presentToday: number;
  absentToday: number;
  eduStaffAbsences: number;
  assistStaffAbsences: number;
  staffAbsences?: number;
  transportersArrived: number;
  lastUpdated?: any;
}

interface ClassAttendanceSummary {
  classId: string;
  className: string;
  total: number;
  present: number;
  imageUrl?: string;
  color?: string;
}

interface DashboardProps {
  setActiveTab: (tab: string, reportType?: string, classId?: string) => void;
  userRole?: string | null;
}

const Dashboard: React.FC<DashboardProps> = ({ setActiveTab, userRole }) => {
  const { selectedYear, currentYearObj } = useAcademicYear();
  const [stats, setStats] = useState<DashboardStats>(() => cachedStatsByYear[selectedYear] || {
    totalStudents: 0,
    presentToday: 0,
    absentToday: 0,
    eduStaffAbsences: 0,
    assistStaffAbsences: 0,
    transportersArrived: 0
  });
  const [classSummaries, setClassSummaries] = useState<ClassAttendanceSummary[]>(() => cachedSummariesByYear[selectedYear] || []);
  const [longTermAbsentees, setLongTermAbsentees] = useState<any[]>([]);
  const [loading, setLoading] = useState(!cachedStatsByYear[selectedYear]);
  const [refreshing, setRefreshing] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  useEffect(() => {
    // When selectedYear changes or on entry, load fresh stats
    fetchStats(true);
  }, [selectedYear]);

  // Listen to daily_stats_cleared broadcast when students/classes are updated
  useEffect(() => {
    const handleStatsCleared = () => {
      clearDashboardCache();
      fetchStats(true);
    };
    window.addEventListener('daily_stats_cleared', handleStatsCleared);
    return () => window.removeEventListener('daily_stats_cleared', handleStatsCleared);
  }, [selectedYear]);

  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const REFRESH_COOLDOWN = 2 * 1000; // 2 seconds cooldown

  const fetchStats = async (forceRefresh = false, isManual = false) => {
    let safetyTimer: any = null;
    if (isManual) {
      const now = Date.now();
      if (now - lastRefreshTime < REFRESH_COOLDOWN) {
        console.log("Refresh cooldown active");
        return;
      }
      setRefreshing(true);
      setLastRefreshTime(now);
      delete cachedStatsByYear[selectedYear];
      delete cachedSummariesByYear[selectedYear];
      safetyTimer = setTimeout(() => {
        setRefreshing(false);
        setLoading(false);
      }, 4000);
    } else if (forceRefresh) {
      delete cachedStatsByYear[selectedYear];
      delete cachedSummariesByYear[selectedYear];
    } else {
      if (!cachedStatsByYear[selectedYear]) {
        setLoading(true);
      }
    }
    setQuotaExceeded(false);
    setPermissionError(false);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const statsData = await getDailyStats(today, selectedYear, forceRefresh);

      if (statsData.isFallback) {
        setQuotaExceeded(true);
      }
      
      const newStats = {
        totalStudents: statsData.totalStudents,
        presentToday: statsData.presentToday,
        absentToday: statsData.absentToday,
        eduStaffAbsences: statsData.eduStaffAbsences,
        assistStaffAbsences: statsData.assistStaffAbsences,
        transportersArrived: statsData.transportersArrived,
        totalTransporters: statsData.totalTransporters,
        lastUpdated: statsData.lastUpdated
      };

      const newSummaries = Object.entries(statsData.classStats || {})
        .map(([id, data]: [string, any]) => ({ classId: id, ...data }))
        .sort((a, b) => (a.className || '').localeCompare(b.className || '', 'he', { numeric: true }));

      setStats(newStats);
      setClassSummaries(newSummaries);
      
      // Fetch long term absences for selected academic year
      try {
        const longTerm = await getLongTermAbsences(selectedYear);
        setLongTermAbsentees(longTerm);
      } catch (ltErr) {
        console.warn("Could not fetch long term absences:", ltErr);
      }
      
      // Update year-specific cache
      cachedStatsByYear[selectedYear] = newStats;
      cachedSummariesByYear[selectedYear] = newSummaries;
      lastFetchTimeByYear[selectedYear] = Date.now();

    } catch (error: any) {
      if (isQuotaError(error)) {
        setQuotaExceeded(true);
        console.warn("Dashboard stats fetch paused: Quota exceeded");
      } else {
        const errMsg = error?.message || String(error);
        if (errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions')) {
          setPermissionError(true);
        }
        console.error("Error fetching dashboard stats:", error);
      }
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      setLoading(false);
      setRefreshing(false);
    }
  };

  const statCards = [
    { label: 'סך תלמידים', value: stats?.totalStudents ?? '...', icon: Users, color: 'bg-blue-50 text-blue-600', id: 'total' },
    { label: 'תלמידים נוכחים', value: stats?.presentToday ?? '...', icon: UserCheck, color: 'bg-emerald-50 text-emerald-600', id: 'present' },
    { label: 'תלמידים נעדרים', value: stats?.absentToday ?? '...', icon: UserMinus, color: 'bg-rose-50 text-rose-600', id: 'absent' },
    { label: 'היעדרות חינוכי טיפולי', value: stats?.eduStaffAbsences ?? '...', icon: UserX, color: 'bg-amber-50 text-amber-600', id: 'staff-edu' },
    { label: 'היעדרות סייעות', value: stats?.assistStaffAbsences ?? '...', icon: UserX, color: 'bg-teal-50 text-teal-600', id: 'staff-assist' },
    { label: 'מסיעים שהגיעו', value: stats?.transportersArrived ?? '...', icon: Truck, color: 'bg-indigo-50 text-indigo-600', id: 'transport' },
  ];

  return (
    <div className="space-y-8" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-100">
            <Home size={28} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-slate-900">עמוד הבית</h2>
              <span className="text-xs font-bold px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100">
                {currentYearObj?.name || selectedYear}
              </span>
            </div>
            <div className="flex items-center gap-2 text-slate-500 text-sm mt-0.5">
              <p>סקירה יומית של נתוני בית הספר</p>
              {stats?.lastUpdated && (
                <span className="text-slate-400">
                  (עודכן לאחרונה: {(() => {
                    try {
                      const d = stats.lastUpdated.toDate ? stats.lastUpdated.toDate() : new Date(stats.lastUpdated);
                      return isNaN(d.getTime()) ? 'לא ידוע' : d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
                    } catch (e) {
                      return 'לא ידוע';
                    }
                  })()})
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => fetchStats(true, true)}
            disabled={refreshing || loading || (Date.now() - lastRefreshTime < REFRESH_COOLDOWN)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-600 rounded-xl border border-slate-100 shadow-sm hover:bg-slate-50 transition-all disabled:opacity-50"
            title={Date.now() - lastRefreshTime < REFRESH_COOLDOWN ? `ניתן לרענן שוב בעוד ${Math.ceil((REFRESH_COOLDOWN - (Date.now() - lastRefreshTime)) / 1000)} שניות` : "רענן נתונים"}
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="text-sm font-bold">
              {refreshing ? 'מרענן...' : 'רענן'}
            </span>
          </button>
          <div className="bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
            <img 
              src="/logo.png" 
              alt="School Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {quotaExceeded && (
        <div className="bg-red-50 border border-red-200 p-6 rounded-3xl flex flex-col items-center text-center gap-4 animate-in fade-in slide-in-from-top-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-full">
            <AlertTriangle size={32} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-900">מכסת השימוש בבסיס הנתונים הסתיימה להיום</h3>
            <p className="text-red-700 text-sm mt-1 max-w-md">
              הגעת למגבלת הקריאות החינמית של Firestore. המכסה תתאפס מחר בבוקר.
              בינתיים, ייתכן שחלק מהנתונים לא יוצגו או לא יהיו מעודכנים.
            </p>
            <button 
              onClick={fetchStats}
              className="mt-4 px-6 py-2 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all text-sm"
            >
              נסה שוב
            </button>
          </div>
        </div>
      )}

      {permissionError && !quotaExceeded && (
        <div className="bg-amber-50 border border-amber-200 p-5 rounded-3xl flex flex-col items-center text-center gap-3 animate-in fade-in slide-in-from-top-4">
          <div className="p-2.5 bg-amber-100 text-amber-600 rounded-full">
            <AlertTriangle size={24} />
          </div>
          <div>
            <h3 className="text-base font-bold text-amber-900">הרשאות צפייה מוגבלות</h3>
            <p className="text-amber-700 text-xs mt-1 max-w-md">
              חלק מהנתונים דורשים הרשאת מנהל מלאה (Admin). אם התחברת דרך חשבון מנהל, נסה לרענן את העמוד או להתחבר שוב.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {statCards.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            onClick={() => {
              if (stat.id === 'staff-edu') setActiveTab('staff-edu');
              if (stat.id === 'staff-assist') setActiveTab('staff-assist');
              if (stat.id === 'transport') setActiveTab('transport');
              if (stat.id === 'present' || stat.id === 'absent' || stat.id === 'total') setActiveTab('attendance');
            }}
            className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex flex-col items-center text-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className={`p-3 rounded-xl ${stat.color}`}>
              <stat.icon size={20} />
            </div>
            <div>
              <p className="text-slate-500 text-[10px] md:text-xs font-bold leading-tight min-h-[2rem] flex items-center justify-center">{stat.label}</p>
              <p className="text-2xl font-black text-slate-900 mt-1">
                {loading ? '...' : stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* General Attendance Report Summary */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <ArrowLeft size={20} className="text-indigo-500" />
            דוח נוכחות כללי להיום
          </h3>
          <button 
            onClick={() => setActiveTab('reports', 'general')}
            className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            לדוח המלא
          </button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse h-20"></div>
            ))
          ) : classSummaries.length > 0 ? (
            classSummaries.map((summary) => (
              <motion.div
                key={summary.classId}
                whileHover={{ scale: 1.01 }}
                onClick={() => setActiveTab('attendance', undefined, summary.classId)}
                className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between cursor-pointer hover:border-indigo-200 transition-all group"
                style={{ borderRight: summary.color ? `4px solid ${summary.color}` : undefined }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl overflow-hidden border border-slate-100 flex-shrink-0"
                    style={{ borderColor: summary.color || '#e2e8f0' }}
                  >
                    {summary.imageUrl ? (
                      <img src={summary.imageUrl} alt={summary.className} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-50 flex items-center justify-center text-slate-300">
                        <School size={18} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-700" style={{ color: summary.color }}>{summary.className}</span>
                    <span className="text-[10px] text-slate-500">נוכחות/סה"כ</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-end">
                    <span className="text-lg font-bold text-indigo-600" style={{ color: summary.color || '#4f46e5' }}>{summary.present} / {summary.total}</span>
                    <div className="w-16 h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full" 
                        style={{ 
                          width: `${(summary.present / (summary.total || 1)) * 100}%`,
                          backgroundColor: summary.color || '#6366f1'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full bg-slate-50 p-6 rounded-2xl text-center text-slate-500 text-sm">
              אין נתוני כיתות להצגה
            </div>
          )}
        </div>
      </div>

      {/* Long Term Absences Section */}
      {longTermAbsentees.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-rose-50 border border-rose-100 rounded-[2.5rem] p-8 space-y-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-500 text-white rounded-2xl shadow-lg shadow-rose-100">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-rose-900">היעדרות ממושכת (3 ימים ומעלה)</h3>
                <p className="text-rose-700 text-sm">תלמידים שנעדרו ברצף ב-3 ימי הלימוד האחרונים</p>
              </div>
            </div>
            <div className="bg-rose-100 text-rose-700 px-4 py-1.5 rounded-full text-sm font-bold">
              {longTermAbsentees.length} תלמידים
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {longTermAbsentees.map((student) => (
              <motion.div
                key={student.id}
                whileHover={{ y: -2 }}
                onClick={() => setActiveTab('attendance', undefined, student.classId)}
                className="bg-white p-4 rounded-2xl border border-rose-100 shadow-sm flex items-center justify-between cursor-pointer hover:border-rose-300 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500">
                    <UserX size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{student.fullName}</p>
                    <p className="text-[10px] text-slate-500">כיתה {student.className}</p>
                  </div>
                </div>
                <div className="text-rose-600 font-black text-xs bg-rose-50 px-2 py-1 rounded-lg">
                  {student.consecutiveDays} ימים
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Welcome Card */}
      <div className="bg-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl shadow-indigo-100">
        <div className="relative z-10 max-w-lg">
          <h3 className="text-2xl font-bold mb-2">ברוכים הבאים למערכת הניהול</h3>
          <p className="text-indigo-100 leading-relaxed">
            המערכת מאפשרת ניהול שוטף של נוכחות תלמידים, מסיעים וצוות. 
            כל הנתונים נשמרים ומסונכרנים בזמן אמת.
          </p>
        </div>
        <School className="absolute -left-8 -bottom-8 text-indigo-500 opacity-20 w-64 h-64" />
      </div>
    </div>
  );
};

export default Dashboard;
